'use strict';

import vex from 'vex-js';
import translator from '../translator';

const config = {
    maxAllowedProductLinks: 5,
    triggerTarget: '.js-product-dialog-trigger',
    dialogContents: '.c-product-dialog',
    formFieldTarget: '.c-product-dialog__form-field',
    addTarget: '.c-product-dialog__add',
    closeTarget: '.c-product-dialog__close',
    saveTarget: '.c-product-dialog__save',
    formTarget: '.o-form_row',
    genericErrorTarget: '.o-error',
    formErrorTarget: '.js-stock-error',
    noResultsTarget: '.c-product-dialog__no-results',
    clearRowTarget: '.c-product-dialog__clear',
    tableTarget: '.js-product-table',
    alliedFieldContainerTarget: '.js-allied-field',
    alliedErrorTarget: '.js-form-error',
    nullText: 'No information available',
    errorClass: 'o-form_row--is-error',
    successClass: 'o-form_row--is-success',
    clickEvent: 'click touch',
    blurEvent: 'blur',
    vexTheme: 'vex-theme-plain',
    dataProductLinks: 'product-links',
    dataProductLookup: 'product-lookup',
    dataSave: 'save',
    dataAlliedSave: 'save-allied-link',
    dataAlliedValidate: 'validate-allied-link',
    dataNodeId: 'node-id',
    dataStockNumber: 'stock-number',
    productLinksUpdatedEventName: 'productLinks.updated',
    productLinksUpdatedEventTarget: 'body',
    hiddenClass: 'is-hidden',
    loadingClass: 'is-loading',
    invalidStockNumberError: translator.getTranslationByKey('productdialog', 'invalid_stock_number_error'),
    duplicateStockNumberError: translator.getTranslationByKey('productdialog', 'duplicate_stock_number_error'),
    maxAllowedProductLinksError: translator.getTranslationByKey('productdialog', 'max_allowed_product_links_error'),
    invalidAlliedNumberError: translator.getTranslationByKey('productdialog', 'invalid_allied_number_error'),
    invalidFormError: translator.getTranslationByKey('productdialog', 'invalid_form_error'),
    loadingFormError: translator.getTranslationByKey('productdialog', 'loading_form_error'),
    saveError: translator.getTranslationByKey('productdialog', 'save_error')
};

class ProductDialog {

    constructor() {
        if(!$(config.triggerTarget).length) {
            return;
        }

        this.setupClickEvents();
    }

    /**
     * Sets up all click events for the ProductDialog component
     */
    setupClickEvents() {
        let that = this;

        $(config.triggerTarget).each(function () {
            let clickTarget = $(this);
            let extraTriggers = clickTarget.attr('data-extra-trigger-targets');

            if (extraTriggers) {
                extraTriggers = extraTriggers.split('|');

                for (let i = 0; i < extraTriggers.length; i++) {
                    $(extraTriggers[i]).on(config.clickEvent, (e) => that.handleOpenDialog(e, clickTarget));
                }
            }

            clickTarget.on(config.clickEvent, (e) => that.handleOpenDialog(e, clickTarget));
        });
    }

    /**
     * Handles trigger for opening the popup
     *
     * @param {event} e
     * @param {object} clickTarget
     */
    handleOpenDialog(e, clickTarget) {
        e.preventDefault();

        this.vex = vex.open({
            content: clickTarget.closest('a').next(config.dialogContents)[0].outerHTML,
            className: config.vexTheme,
            showCloseButton: false
        });

        this.loadTableData();

        $(config.addTarget, this.vex[0]).on(config.clickEvent, (e) => this.handleAddProduct(e));
        $(config.closeTarget, this.vex[0]).on(config.clickEvent, (e) => this.handleCloseDialog(e));
        $(config.saveTarget, this.vex[0]).on(config.clickEvent, (e) => this.handleSaveAndCloseDialog(e));
    }

    /**
     * Loads the table data via ajax
     */
    loadTableData() {
        const url = $(config.dialogContents, this.vex[0]).data(config.dataProductLinks);

        let that = this;

        $.ajax({
            url: url,
            dataType: 'json',
            statusCode: {
                500: function () {
                    alert(config.saveError);
                },
                400: function () {
                    alert(config.saveError);
                }
            },
            success: function (response) {
                for(var k in response) {
                    if (response[k].product === undefined || !response[k].product.stockNumber) {
                        // When we dont have a product, we can fall back to only displaying its number
                        that.addTableRow(response[k].rs_stock_number, config.nullText, config.nullText, config.nullText);
                        continue;
                    }

                    const stockNumber = response[k].product.stockNumber ? response[k].product.stockNumber : response[k].rs_stock_number;
                    const name = response[k].product.name;
                    const alliedPartNumber = response[k].product.alliedPart ? response[k].product.alliedPart.alliedPartNumber : null;
                    const manufacturerPartNumber = response[k].product.manufacturerPartNumber;

                    that.addTableRow(stockNumber, name, alliedPartNumber, manufacturerPartNumber);
                }
            }
        });
    }

    /**
     * Handler for the add product trigger
     *
     * @param {event} e
     */
    handleAddProduct(e) {
        e.preventDefault();
        const submittedNumber = $(config.formFieldTarget, this.vex[0]).val();
        let status = this.validateStockNumber(submittedNumber);

        if (status !== true) {
            this.showError(status);
        } else {
            const normalisedNumber = this.normaliseStockNumber(submittedNumber);
            const url = $(config.dialogContents, this.vex[0]).data(config.dataProductLookup) + normalisedNumber;

            let that = this;

            that.hideError();

            $.ajax({
                url: url,
                dataType: 'json',
                statusCode: {
                    404: function () {
                        that.addTableRow(normalisedNumber, config.nullText, config.nullText, config.nullText);
                    },
                    400: function (jqXHR) {
                        that.showError(jqXHR.responseText);
                    }
                },
                success: function (response) {
                    const stockNumber = response.stockNumber ? response.stockNumber : normalisedNumber;
                    const name = response.name;
                    const alliedPartNumber = response.alliedPart ? response.alliedPart.alliedPartNumber : null;
                    const manufacturerPartNumber = response.manufacturerPartNumber;

                    that.addTableRow(stockNumber, name, alliedPartNumber, manufacturerPartNumber);
                }
            });
        }
    }

    /**
     * Validate a stock number.
     *
     * @param {string} stockNumber The stock number.
     *
     * @return {bool|string} True if the stock number is valid, or an error message if it is not.
     */
    validateStockNumber(stockNumber) {
        const normalised = this.normaliseStockNumber(stockNumber);

        let status = true;
        let tableData = [];

        if (/^\d{6,7}$/.test(normalised) === false) {
            status = config.invalidStockNumberError;
        }

        tableData = this.compileTableData();

        if (tableData.indexOf(normalised) !== -1) {
            status = config.duplicateStockNumberError;
        }

        if (tableData.length >= config.maxAllowedProductLinks) {
            status = config.maxAllowedProductLinksError;
        }

        return status;
    }

    /**
     * Normalises a stock number by removing hyphens and whitespace.
     *
     * @param {string} stockNumber The part number.
     *
     * @return {string|null} The normalised stock number, or null if it could not be normalised.
     */
    normaliseStockNumber(stockNumber) {
        return stockNumber.replace(/[-\s]/g, '');
    }

    /**
     * Removes the error status
     */
    hideError() {
        $(config.formTarget).removeClass(config.errorClass);
        $(config.formErrorTarget).addClass(config.hiddenClass);
    }

    /**
     * Shows the error message passed through near the input box
     *
     * @param {string} message
     */
    showError(message) {
        $(config.formTarget).addClass(config.errorClass);
        $(config.formErrorTarget).html(message).removeClass(config.hiddenClass);
    }

    /**
     * Adds the table row with the given values as the row contents
     *
     * @param {string} stockNumber
     * @param {string} name
     * @param {string} alliedPartNumber
     * @param {string} manufacturerPartNumber
     */
    addTableRow(stockNumber, name, alliedPartNumber, manufacturerPartNumber) {
        if (!name) {
            name = config.nullText;
        }

        if (!manufacturerPartNumber) {
            manufacturerPartNumber = config.nullText;
        }

        console.log("Adding row:", stockNumber, name, alliedPartNumber, manufacturerPartNumber);
        const tableRow = this.getTableRowHtml(stockNumber, name, alliedPartNumber, manufacturerPartNumber);
        let table = $(config.tableTarget, this.vex[0]);

        // Show table if it is hidden
        table.removeClass(config.hiddenClass);
        $(config.noResultsTarget, this.vex[0]).addClass(config.hiddenClass);

        $('tbody:last-child', table).append(tableRow);
        $(config.clearRowTarget, this.vex[0]).on(config.clickEvent, (e) => this.handleClearRow(e));
        $(config.alliedFieldContainerTarget + ' input', table).off(config.blurEvent).on(config.blurEvent, (e) => this.handleValidateAlliedProduct(e));

    }

    /**
     * Removes the row which contains the button which triggered this event
     *
     * @param {event} e
     */
    handleClearRow(e) {
        e.preventDefault();

        let table = $(config.tableTarget, this.vex[0]);

        // Clear the row.
        $(e.target).closest('tr').remove();

        // Toggle between the table and empty indicator if the table is empty
        if (!$('tbody tr', table).length) {
            table.addClass(config.hiddenClass);
            $(config.noResultsTarget, this.vex[0]).removeClass(config.hiddenClass);
        }
    }

    /**
     * Handler for when close is clicked
     *
     * @param {event} e
     */
    handleCloseDialog(e) {
        e.preventDefault();
        this.closeDialog();
    }

    /**
     * Handler for when save is clicked, posts the data to the given URL before quitting.
     *
     * @param {event} e
     */
    handleSaveAndCloseDialog(e) {
        e.preventDefault();

        const dialogContents = $(config.dialogContents, this.vex[0]);
        const $formError = $(config.alliedErrorTarget, dialogContents);

        $formError.addClass(config.hiddenClass);

        if (!this.isValid()) {
            $formError.html(config.invalidFormError).removeClass(config.hiddenClass);
            return;
        }

        if (!this.isLoading()) {
            $formError.html(config.loadingFormError).removeClass(config.hiddenClass);
            return;
        }

        this.saveProductLinks(dialogContents);
        this.saveAlliedProductRelations(dialogContents);
    }

    /**
     * Saves the product links
     *
     * @param {jQuery} dialog
     */
    saveProductLinks(dialog) {

        const url = dialog.data(config.dataSave);
        const data = this.compileTableData();
        let that = this;

        $.ajax({
            url: url,
            data: JSON.stringify(data),
            processData: false,
            dataType: 'json',
            type: 'post',
            statusCode: {
                500: function () {
                    alert(config.saveError);
                },
                400: function () {
                    alert(config.saveError);
                }
            },
            success: function () {
                that.closeDialog();

                $(config.productLinksUpdatedEventTarget).trigger(
                    config.productLinksUpdatedEventName,
                    {
                        nodeId: dialog.data(config.dataNodeId),
                        productLinks: data
                    }
                );
            }
        });

    }

    /**
     * Optimistically saves the product links
     *
     * @param {jQuery} dialog
     */
    saveAlliedProductRelations(dialog) {

        const url = dialog.data(config.dataAlliedSave);
        const data = this.compileAlliedLinks();

        $.ajax({
            url: url,
            data: data,
            type: 'post',
            statusCode: {
                500: function (jqXHR) {
                    alert(jqXHR.responseText);
                },
                400: function (jqXHR) {
                    alert(jqXHR.responseText);
                }
            }
        });
    }

    /**
     * Handler for when an allied product is saved
     *
     * @param {event} e
     */
    handleValidateAlliedProduct(e) {
        e.preventDefault();

        if (!e.target.value) {
            return;
        }

        const dialogContents = $(config.dialogContents, this.vex[0]);
        const url = dialogContents.data(config.dataAlliedValidate) + e.target.value;
        const $input = $(e.target);
        const $formRow = $input.closest(config.alliedFieldContainerTarget);
        const $errorMessage = $formRow.find(config.genericErrorTarget);
        const showError = function() {
            $formRow.addClass(config.errorClass).removeClass(config.loadingClass);
            $errorMessage.html(config.invalidAlliedNumberError).removeClass(config.hiddenClass);
        };

        $formRow.removeClass(config.errorClass).removeClass(config.successClass).addClass(config.loadingClass);
        $errorMessage.addClass(config.hiddenClass);

        $.ajax({
            url: url,
            method: 'GET',
            dataType: 'json',
            error: showError,
            success: function (response) {
                if (response.status) {
                    $formRow.addClass(config.successClass).removeClass(config.loadingClass);
                } else {
                    showError();
                }
            }
        });
    }


    /**
     * Returns an array of all the stock numbers in the table
     *
     * @returns {Array}
     */
    compileTableData() {
        const table = $(config.tableTarget, this.vex[0])[0];
        let data = [];

        // go through cells
        for (var i=1; i<table.rows.length; i++) {
            const tableRow = table.rows[i];
            data.push(tableRow.dataset.stockNumber);
        }

        return data;
    }

    /**
     * Returns an object containing stock number to allied relations
     *
     * @return {boolean|object}
     */
    compileAlliedLinks() {
        const $alliedFields = $(config.alliedFieldContainerTarget, this.vex[0]);

        if (!$alliedFields.length) {
            return false;
        }

        let data = {};

        $alliedFields.each(function () {
            const $this = $(this);
            const stockNumber = $this.data(config.dataStockNumber);

            data[stockNumber] = $('input', $this).val();
        });

        return data;
    }

    /**
     * Returns a html string for a table row containing the dat given
     *
     * @param {string} stockNumber
     * @param {string} name
     * @param {string} alliedPartNumber
     * @param {string} manufacturerPartNumber
     * @returns {string}
     */
    getTableRowHtml(stockNumber, name, alliedPartNumber, manufacturerPartNumber) {
        if (!alliedPartNumber) {
            alliedPartNumber = `
            <div class="o-form_row js-allied-field" data-stock-number="${stockNumber}">
                <div class="o-input o-input--themed">
                    <input type="text" name="allied[${stockNumber}]">
                    <div class="o-error is-hidden"></div>
                </div>
            </div>`
        }

        return `
            <tr data-stock-number="${stockNumber}">
              <td>${name}</td>
              <td>${stockNumber}</td>
              <td>${alliedPartNumber}</td>
              <td>${manufacturerPartNumber}</td>
              <td><a href="#" class="${config.clearRowTarget.substring(1)}">&nbsp</a></td>
            </tr>`;
    }

    /**
     * Closes the dialog associated with this instance
     */
    closeDialog() {
        vex.close(this.vex.data().vex.id);
    }

    /**
     * Returns true is dialog is in a valid state to be saved.
     *
     * @returns {boolean}
     */
    isValid() {

        const $table = $(config.tableTarget, this.vex[0]);
        const $alliedInputs = $(config.alliedFieldContainerTarget, $table);

        return !$alliedInputs.hasClass(config.errorClass);
    }

    /**
     * Returns true is dialog is loading allied product information.
     *
     * @returns {boolean}
     */
    isLoading() {

        const $table = $(config.tableTarget, this.vex[0]);
        const $alliedInputs = $(config.alliedFieldContainerTarget, $table);

        return !$alliedInputs.hasClass(config.loadingClass);
    }
}

$(window).load(function(){
    new ProductDialog();
});



// WEBPACK FOOTER //
// ./src/js/components/product-dialog.js